import { TFunction } from "i18next";

const NETWORK_ERROR_PATTERNS = [
  "failed to fetch",
  "networkerror",
  "network error",
  "load failed",
  "network request failed",
  "err_network",
  "err_internet_disconnected",
  "err_connection",
  "the internet connection appears to be offline",
];

function getRawMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "";
}

export function isNetworkError(error: unknown): boolean {
  const message = getRawMessage(error).toLowerCase();
  return (
    message.length > 0 &&
    NETWORK_ERROR_PATTERNS.some(pattern => message.includes(pattern))
  );
}

export function handleApiError(error: unknown, t: TFunction): string {
  const formatMessage = (msg: string): string => {
    return msg.replace(/;\s*/g, "\n");
  };

  // Map an HTTP status embedded in an error/message string (e.g. the Feign tunnel format
  // "[400 ] during [GET] ...") to a localized message, so raw status strings never reach the user.
  const translateStatusInText = (text: string): string | undefined => {
    const match = text.match(/\[(\d{3})\s*\]/);
    const status = match ? Number(match[1]) : undefined;
    if (status === 401) return t("error.unauthorized");
    if (status === 403) return t("error.forbidden");
    if (status === 404) return t("error.notFound");
    if (status && status >= 400 && status < 500)
      return t("error.badRequest", { status });
    if (status && status >= 500) return t("error.serverError");
    // Preserve the previous lenient bare-number matches.
    if (text.includes("403")) return t("error.forbidden");
    if (text.includes("404")) return t("error.notFound");
    return undefined;
  };

  if (isNetworkError(error)) {
    return t("error.network");
  }

  if (typeof error === "string") {
    const mapped = translateStatusInText(error);
    if (mapped) return mapped;
    return error.length < 200 ? formatMessage(error) : t("error.errorOccurred");
  }

  if (error && typeof error === "object") {
    const errObj = error as Record<string, unknown>;

    const getStatus = (obj: Record<string, unknown>): number | undefined => {
      if (typeof obj.status === "number") return obj.status;
      if (typeof obj.status === "string") return Number(obj.status);

      const response = obj.response as Record<string, unknown> | undefined;
      if (response && typeof response.status === "number")
        return response.status;

      const nestedError = obj.error as Record<string, unknown> | undefined;
      if (nestedError && typeof nestedError.status === "number")
        return nestedError.status;

      return undefined;
    };

    const status = getStatus(errObj);

    if (status && status > 0) {
      if (status === 401) {
        return t("error.unauthorized");
      }
      if (status === 403) {
        return t("error.forbidden");
      }
      if (status === 404) {
        return t("error.notFound");
      }
      if (status >= 400 && status < 500) {
        return t("error.badRequest", { status });
      }
      if (status >= 500) {
        return t("error.serverError");
      }
    }

    const getBody = (
      obj: Record<string, unknown>
    ): Record<string, unknown> | undefined => {
      if (obj.body && typeof obj.body === "object")
        return obj.body as Record<string, unknown>;
      if (obj.response && typeof obj.response === "object") {
        const response = obj.response as Record<string, unknown>;
        if (response.data && typeof response.data === "object")
          return response.data as Record<string, unknown>;
      }
      if (obj.error && typeof obj.error === "object")
        return obj.error as Record<string, unknown>;
      return undefined;
    };

    const body = getBody(errObj);
    if (body) {
      if (Array.isArray(body.errors)) {
        return body.errors
          .map((e: unknown) => {
            if (typeof e === "string") return e;
            if (
              e &&
              typeof e === "object" &&
              "message" in e &&
              typeof e.message === "string"
            ) {
              return e.message;
            }
            return "";
          })
          .filter(Boolean)
          .join("\n");
      }
      if (typeof body.message === "string" && body.message) {
        return (
          translateStatusInText(body.message) ?? formatMessage(body.message)
        );
      }
      if (typeof body.detail === "string" && body.detail) {
        return translateStatusInText(body.detail) ?? formatMessage(body.detail);
      }
      if (typeof body.error === "string" && body.error) {
        return translateStatusInText(body.error) ?? formatMessage(body.error);
      }
    }

    if (typeof errObj.message === "string" && errObj.message) {
      return (
        translateStatusInText(errObj.message) ?? formatMessage(errObj.message)
      );
    }
  }

  if (error instanceof Error) {
    return translateStatusInText(error.message) ?? formatMessage(error.message);
  }

  const stringErr = String(error);
  if (stringErr.includes("[404 ]") || stringErr.includes("404")) {
    return t("error.notFound");
  }

  return t("error.unknown");
}

export function isNotFoundError(error: unknown): boolean {
  if (typeof error === "string") {
    return error.includes("[404 ]") || error.includes("404");
  }

  if (error && typeof error === "object") {
    const errObj = error as Record<string, unknown>;

    const getStatus = (obj: Record<string, unknown>): number | undefined => {
      if (typeof obj.status === "number") return obj.status;
      if (typeof obj.status === "string") return Number(obj.status);

      const response = obj.response as Record<string, unknown> | undefined;
      if (response && typeof response.status === "number")
        return response.status;

      const nestedError = obj.error as Record<string, unknown> | undefined;
      if (nestedError && typeof nestedError.status === "number")
        return nestedError.status;

      return undefined;
    };

    const status = getStatus(errObj);
    if (status === 404) return true;

    const stringRepresentation = String(error);
    if (
      stringRepresentation.includes("[404 ]") ||
      stringRepresentation.includes("404")
    ) {
      return true;
    }

    const getMessage = (obj: Record<string, unknown>): string | undefined => {
      if (typeof obj.message === "string") return obj.message;

      const nestedError = obj.error as Record<string, unknown> | undefined;
      if (nestedError && typeof nestedError.message === "string")
        return nestedError.message;

      const body = obj.body as Record<string, unknown> | undefined;
      if (body && typeof body.message === "string") return body.message;

      const response = obj.response as Record<string, unknown> | undefined;
      if (
        response &&
        response.data &&
        typeof (response.data as Record<string, unknown>).message === "string"
      ) {
        return (response.data as Record<string, unknown>).message as string;
      }

      return undefined;
    };

    const message = getMessage(errObj);
    if (message && (message.includes("[404 ]") || message.includes("404"))) {
      return true;
    }
  }
  return false;
}
